import { UserPermission } from '@studio/domain/user';
import { BrandLibraryHotkeys } from './brand-library.hotkeys';
import { EditorHotkeys } from './editor.hotkeys';
import { ManagePageHotkeys } from './manage-page.hotkeys';
import { TextEditModeHotkeys } from './text-edit.hotkeys';
import { TimelineHotkeys } from './timeline.hotkeys';
import { TranslationPageHotkeys } from './translation-page.hotkeys';
import { WorkspaceHotkeys } from './workspace.hotkeys';

export const HOTKEYS: IHotkey[] = [
    ...TextEditModeHotkeys,
    ...EditorHotkeys,
    ...BrandLibraryHotkeys,
    ...WorkspaceHotkeys,
    ...TimelineHotkeys,
    ...ManagePageHotkeys,
    ...TranslationPageHotkeys,
    {
        command: 'Escape',
        keyCombination: 'Escape',
        context: 'EditElement',
        meta: { description: 'Escape current context', shortcut: 'Escape' }
    },
    {
        command: 'SaveWidget',
        keyCombination: { mac: 'Cmd + S', win: 'Ctrl + S' },
        context: 'WidgetEditor',
        meta: { description: 'Save widget', shortcut: { mac: '⌘ + S', win: 'Ctrl + S' } }
    },
    {
        command: 'ZoomTimeline',
        keyCombination: { win: 'Ctrl + Wheel', mac: 'Cmd + Wheel' },
        context: 'Timeline',
        meta: { description: 'Zoom with scroll', shortcut: { mac: '⌘ + scroll', win: 'Ctrl + scroll' } }
    }
];

const isMac = typeof window !== 'undefined' && navigator.userAgent.includes('Mac');
const hotkeyList = {} as HotkeyCommandList;
HOTKEYS.forEach(hotkey => {
    if (hotkey && hotkey.meta) {
        const contexts = Array.isArray(hotkey.context) ? hotkey.context : [hotkey.context];
        for (const context of contexts as OneOfHotkeyContext[]) {
            hotkeyList[context] = hotkeyList[context] || {};
            if (
                typeof hotkey.meta.shortcut === 'object' &&
                hotkey.meta.shortcut.mac &&
                hotkey.meta.shortcut.win
            ) {
                hotkeyList[context][hotkey.command] = isMac
                    ? hotkey.meta.shortcut.mac
                          .split('+')
                          .join('')
                          .replace(/ /g, '')
                          .replace(/Alt/gi, '⌥')
                    : hotkey.meta.shortcut.win;
            } else if (typeof hotkey.meta.shortcut === 'string') {
                hotkeyList[context][hotkey.command] = hotkey.meta.shortcut;
            }
        }
    }
});

type HotkeyCommandList = { [key in OneOfHotkeyContext]: { [command in HotkeyCommands]: string } };

/**
 *
 * Returns an object with hotkeys (key: value) e.g:
 * ```
 * {
 *      Editor.AlignTop,
 *      Editor.DeleteElement,
 *      Editor.SelectAll
 * }
 * ```
 */
export function getHotkeysAsKeyValueList(contexts: OneOfHotkeyContext[]): HotkeyCommandList {
    const filteredList = {} as HotkeyCommandList;
    contexts.forEach(context => {
        filteredList[context] = hotkeyList[context];
    });
    return filteredList;
}

export type HotkeyCommands =
    | 'NextField'
    | 'PreviousField'
    | 'SaveFieldValue'
    | 'UnfocusField'
    | 'OpenContentPanel'
    | 'OpenStylingPanel'
    | 'LeftAlignedPanel'
    | 'RightAlignedPanel'
    | 'PreviousElement'
    | 'NextElement'
    | 'ToggleAllElements'
    | 'ToggleOtherSizes'
    | 'ExitTranslationPage'
    | 'SelectTextToNextWord'
    | 'OpenCreativeInNewTab'
    | 'SelectTextToPreviousWord'
    | 'SelectTextToStartOfText'
    | 'SelectTextToEndOfText'
    | 'MoveCaretToStartOfText'
    | 'MoveCaretToEndOfText'
    | 'MoveCaretToStartOfParagraph'
    | 'MoveCaretToEndOfParagraph'
    | 'SelectTextToStartOfParagraph'
    | 'SelectTextToEndOfParagraph'
    | 'SelectTextToStartOfLine'
    | 'SelectTextToEndOfLine'
    | 'MoveCaretToPreviousWord'
    | 'MoveCaretToNextWord'
    | 'ToggleBoldTextStyle'
    | 'ToggleItalicTextStyle'
    | 'ToggleUnderlineTextStyle'
    | 'MoveCaretToStartOfLine'
    | 'MoveCaretToEndOfLine'
    | 'MoveCaretForward'
    | 'MoveCaretBackward'
    | 'MoveCaretUpward'
    | 'MoveCaretDownward'
    | 'SelectTextForward'
    | 'SelectTextBackward'
    | 'SelectTextUpward'
    | 'SelectTextDownward'
    | 'SelectTextToCurrentClick'
    | 'SelectAllText'
    | 'EraseTextForward'
    | 'EraseTextBackward'
    | 'EraseWord'
    | 'EraseToStartOfLine'
    | 'Escape'
    | 'UndoTextChange'
    | 'RedoTextChange'
    | 'CopySelectedText'
    | 'CutSelectedText'
    | 'PasteSelectedText'
    | 'Save'
    | 'SaveAndExit'
    | 'SaveOnTextEditMode'
    | 'SaveAndExitOnTextEditMode'
    | 'Undo'
    | 'Redo'
    | 'Deselect'
    | 'Copy'
    | 'Paste'
    | 'PasteStyle'
    | 'PasteLayout'
    | 'PasteAnimation'
    | 'PasteDesign'
    | 'Cut'
    | 'SelectAll'
    | 'AlignLeft'
    | 'AlignRight'
    | 'AlignCenter'
    | 'AlignTop'
    | 'AlignBottom'
    | 'AlignMiddle'
    | 'DistributeHorizontally'
    | 'DistributeVertically'
    | 'OpenBrandLibrary'
    | 'OpenImageLibrary'
    | 'OpenVideoLibrary'
    | 'OpenWidgetLibrary'
    | 'OpenFeedsLibrary'
    | 'FindInLibrary'
    | 'SaveWidget'
    | 'EnableZoom'
    | 'EnableZoomOut'
    | 'DisableZoom'
    | 'ZoomIn'
    | 'ZoomOut'
    | 'ZoomWheel'
    | 'ZoomPreview'
    | 'ZoomPreviewEnd'
    | 'ZoomToBox'
    | 'ZoomReset'
    | 'PanOn'
    | 'PanOff'
    | 'CreateTextElement'
    | 'CreateButtonElement'
    | 'CreateRectangleElement'
    | 'CreateEllipseElement'
    | 'GroupElements'
    | 'UngroupElements'
    | 'ToggleSelectionTool'
    | 'ToggleLock'
    | 'ToggleVisibility'
    | 'MoveElementUp'
    | 'MoveElementDown'
    | 'MoveElementFront'
    | 'MoveElementBack'
    | 'DeleteElement'
    | 'DeleteKeyframe'
    | 'NudgeLeft'
    | 'NudgeRight'
    | 'NudgeUp'
    | 'NudgeDown'
    | 'MoveLeft'
    | 'MoveRight'
    | 'MoveUp'
    | 'MoveDown'
    | 'HideOtherSizes'
    | 'DeleteSize'
    | 'ApplyFilter'
    | 'ZoomTimeline'
    | 'PlayPause'
    | 'RecordAnimation'
    | 'Clone';

const defmod = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac') ? 'Cmd' : 'Ctrl';
export const BrowserDefaultHotkeys = {
    SelectAll: `${defmod} + A`,
    Save: `${defmod} + S`,
    SaveAndExit: `${defmod} + Shift + S`,
    Cut: `${defmod} + X`,
    PasteStyle: `${defmod} + Shift + V`,
    OpenLocation: `${defmod} + L`,
    ShowFullHistory: `${defmod} + Y`,
    HideOrHistory: `${defmod} + H`,
    Undo: `${defmod} + Z`,
    Redo: `${defmod} + Shift + Z`,
    NewTab: `${defmod} + T`,
    NewWindow: `${defmod} + N`,
    CloseTab: `${defmod} + W`,
    Find: `${defmod} + F`,
    FindNext: `${defmod} + G`,
    FindPrevious: `${defmod} + Shift + G`,
    ZoomIn: `${defmod} + Plus`,
    ZoomOut: `${defmod} + Minus`,
    ZoomWheel: `${defmod} + Wheel`,
    Help: `${defmod} + ?`,
    OpenSource: `${defmod} + U`,
    GoBack:
        typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox')
            ? 'Backspace'
            : 'Alt + ArrowLeft',
    GoForward: 'Alt + ArrowRight'
};

export type OneOfHotkeyContext =
    | 'ManagePage'
    | 'TranslationPage'
    | 'Showcase'
    | 'Workspace'
    | 'Editor'
    | 'TextEditMode'
    | 'TextElementSelected'
    | 'BrandLibrary'
    | 'EditElement'
    | 'WidgetEditor'
    | 'Timeline';

export interface IOsSpecificKeyCombination {
    win?: string;
    mac?: string;
}

export interface IHotkeyMeta {
    description: string;
    shortcut: string | IOsSpecificKeyCombination;
}

export interface IHotkey {
    keyCombination: string | IOsSpecificKeyCombination;
    command: HotkeyCommands;
    context: OneOfHotkeyContext | OneOfHotkeyContext[];
    category?: string | string[];
    shouldBeEnabledForEmployeeOnly?: boolean;
    permission?: UserPermission;

    /**
     * @summary
     * Specify whether the current command should be returned immediately. Or let the hotkey service
     * continue searching for matching commands.
     *
     * @default
     * true
     */
    returnCommandImmediately?: boolean;

    /**
     * @summary
     * Determines whether a command can be resolved on a context other than the current
     * one. Default value is 'true'.
     *
     * @description
     * If a context is on top of the stack, don't find any matching key combinations.
     * It will try to find on the lower contexts, for any matching keys. If this flag
     * is set to false it won't resolve a command if it finds a matching key combination
     * on lower contexts than the top one(i.e. the current one).
     */
    resolveOnPropogation?: boolean;

    meta?: IHotkeyMeta;
}

export const controlKeys = ['Ctrl', 'Cmd', 'Alt', 'Meta', 'Shift'];

export const ignoredKeys = ['CapsLock'];

export const charCodeExclusion = [
    'Control',
    'AltGraph',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    '+',
    '-',
    '§',
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'Backspace',
    'Escape',
    'Delete'
];

interface IKeyCodeMap {
    [key: string]: string;
}

export const keyCodeMap: IKeyCodeMap = {
    Control: 'Ctrl'
};
